import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { documentVersion } from "@ferrivia/questionnaire-ui";
import { useApi, useTracker } from "@ferrivia/auth-web";
import { useDelayedFlag, useOnline } from "@ferrivia/ui";
import {
  ApiError,
  firstUnfinishedSection,
  INSTRUMENT_DEFINITIONS,
  sectionProgress,
  type AnswerMap,
  type AnswerValue,
  type ItemFieldMap,
  type ItemMap,
  type SectionProgress,
} from "@ferrivia/api-contract";
import type {
  ClientQuestionnaireDetail,
  InstrumentName,
  QuestionnaireAnswer,
  QuestionnaireDetail,
} from "@ferrivia/api-contract";

export type QuestionnaireViewState = "loading" | "consent" | "ready" | "submitted" | "error";

// Immediate on change: the option is picked, there is nothing more to type.
// Debounced: text/number keep changing under the client's fingers, so every
// keystroke sending a request would be wasteful and would lose the "last
// write wins" ordering the plan asks for. A repeating-group item never goes
// through this: the item editor commits it whole (`saveItem`), immediately.
const IMMEDIATE_KINDS = new Set(["choice", "multi_choice", "boolean", "date"]);
const DEBOUNCE_MS = 800;

export type SaveState = "saved" | "saving" | "offline" | "failed";

// One repeating-group item, kept with its wire `itemIndex` attached rather
// than relying on array position -- `deleteAnswerItem` does not re-compact
// indices after a removal (see its own comment), so a later item's index can
// be higher than its position in this array once an earlier item has been
// removed.
interface ItemEntry {
  itemIndex: number;
  fields: ItemFieldMap;
}

// A dirty/invalid item field is keyed by all three of question, item and
// field so two different repeating-group questions (or two items of the
// same one) can each carry their own independent dirty/invalid state. ":"
// can't appear in any of the three parts (question and field keys are
// lower_snake_case contract identifiers; itemIndex is a number rendered by
// String()), so splitting on it is safe.
const ITEM_FIELD_KEY_SEPARATOR = ":";

function itemFieldKey(questionKey: string, itemIndex: number, fieldKey: string): string {
  return [questionKey, itemIndex, fieldKey].join(ITEM_FIELD_KEY_SEPARATOR);
}

function sameAnswerValue(a: AnswerValue | undefined, b: AnswerValue): boolean {
  return a !== undefined && JSON.stringify(a) === JSON.stringify(b);
}

function parseItemFieldKey(key: string): { questionKey: string; itemIndex: number; fieldKey: string } {
  const [questionKey, itemIndexRaw, fieldKey] = key.split(ITEM_FIELD_KEY_SEPARATOR);
  return { questionKey, itemIndex: Number(itemIndexRaw), fieldKey };
}

export interface UseQuestionnaireResult {
  state: QuestionnaireViewState;
  version: number;
  answers: AnswerMap;
  items: ItemMap;
  /** Item indices, in the same order and at the same array positions as the
      corresponding `items.get(questionKey)` array -- carried separately
      because `ItemMap`'s own shape (the engine's, shared with apps/api) is
      just fields, not wire identity. `RepeatingGroup` needs the real index
      to address `saveItem`/`removeItem` once items can have gaps. */
  itemIndexes: ReadonlyMap<string, readonly number[]>;
  /** Per-section answered/reachable counts, in instrument order -- the
      hub's `4/6 vastattu` rows. */
  sections: SectionProgress[];
  /** Whether the *loaded* questionnaire already had any saved answers or
      repeating-group items -- the overlay's landing decision (straight into
      section 1 vs. the hub). Derived from the load payload rather than from
      `localAnswers`, or it would flip to `true` the moment the client
      answers their first question. `detail.status` doesn't work for this:
      `start` moves status to `in_progress` at consent, before a single
      answer exists. */
  hasSavedAnswers: boolean;
  /** The hub's primary-button target: the first section with a reachable
      question left unanswered, or null once every one is answered. */
  firstUnfinishedSectionId: string | null;
  /** Sections that read as done: every reachable question answered, or --
      for a section with nothing that counts toward completion
      (additional_information) -- opened at least once this round. */
  completeSectionIds: ReadonlySet<string>;
  /** Sections opened at least once this round, persisted on the device. */
  visitedSectionIds: ReadonlySet<string>;
  markSectionVisited(sectionId: string): void;
  lastSavedAt: string | null;
  /** The required symptom step: a full check-in recorded since the open
      submission started. Server-derived on load, set locally on save. */
  symptomCheckDone: boolean;
  markSymptomCheckDone(): void;
  /** Opens a submission when none is open yet, so a symptom check-in saved
      next postdates its `started_at`. A no-op once one is open. */
  ensureStarted(): Promise<void>;
  save: SaveState;
  /** Keys the server rejected as `invalid_answer` on the last flush --
      dropped from the dirty set so they can't wedge the queue (see
      `setAnswer`), surfaced here so a question can show a field-level
      error instead of silently losing the edit. Cleared the next time that
      key is written. A top-level question is keyed by its own `questionKey`;
      a repeating-group field is keyed by `itemFieldKey(questionKey,
      itemIndex, fieldKey)`, so both share one set. */
  invalidKeys: ReadonlySet<string>;
  /** Keys showing a value carried over from onboarding (the server's
      `prefill`) that the client hasn't changed yet -- the question's
      "Esitäytetty" hint. Dropped the first time that key is written. */
  prefilledKeys: ReadonlySet<string>;
  /** Records consent and moves past the gate. Nothing is written before this
      resolves -- the questionnaire's own write path 403s without consent, so
      there is no earlier moment a client could reach the hub from. */
  grantConsent(): Promise<void>;
  /** Optimistic: updates local state first and never rolls it back on a
      network failure -- the value stays on screen and stays dirty for
      retry. Immediate or debounced depending on the question's kind. */
  setAnswer(questionKey: string, value: AnswerValue): void;
  /** Removes an item immediately, both locally and on the server
      (`DELETE .../items/:questionKey/:itemIndex`) -- a hard delete, not a
      redaction, matching the plan's "add/remove of a repeating-group item"
      firing immediately. Optimistic and, on a network failure, not rolled
      back: the item stays removed from view and the delete is not retried
      automatically -- there is no "dirty removal" queue, only dirty values,
      so `retrySave` (which only replays `saveAnswers`) cannot repeat it. A
      `save_failed` event still fires so the chip's `failed` state is
      visible; re-opening the questionnaire is the recovery path, since the
      item reappears from the server's own state and can be removed again. */
  removeItem(questionKey: string, itemIndex: number): void;
  /** Commits a whole item from the full-screen item editor in one write:
      `itemIndex: null` appends a new item at the next free index, a number
      edits that item. Only fields whose value differs from the stored one go
      dirty, and they flush immediately rather than debounced -- the editor's
      Tallenna is the commit, there is nothing left being typed. Returns
      false when nothing differed, so there was nothing to write. */
  saveItem(questionKey: string, itemIndex: number | null, fields: ReadonlyMap<string, AnswerValue>): boolean;
  /** Flushes any dirty keys and waits for the write (and any write it
      queues behind it) to settle. Await before leaving a section, closing
      the overlay, or submitting. */
  flush(): Promise<void>;
  /** The autosave chip's tap target in the `failed` state. */
  retrySave(): void;
  /** Re-runs the initial load, for the error state's retry control. */
  retry(): void;
  /** Flushes any dirty keys, then submits. On success moves to `submitted`.
      A 409 `already_submitted` (e.g. a second tab submitted first) is
      treated the same as success -- also `submitted`, nothing rethrown. Any
      other rejection (400 `empty_submission`, a network failure) is
      rethrown for the caller (`HubScreen`) to show inline; state stays
      `ready` so Lähetä can be retried. */
  submit(): Promise<void>;
}

// Device-local, not server state: only an optional section with nothing to
// answer (additional_information) reads it, and "the client has seen this
// page" isn't worth a column. Cleared whenever a fresh round starts or the
// current one is submitted, so it never carries into the next questionnaire.
function visitedStorageKey(instrument: InstrumentName): string {
  return `ferrivia.questionnaire.visited.${instrument}`;
}

function readVisited(instrument: InstrumentName): Set<string> {
  try {
    const raw = window.localStorage.getItem(visitedStorageKey(instrument));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : []);
  } catch {
    return new Set();
  }
}

function writeVisited(instrument: InstrumentName, visited: ReadonlySet<string>): void {
  try {
    if (visited.size === 0) window.localStorage.removeItem(visitedStorageKey(instrument));
    else window.localStorage.setItem(visitedStorageKey(instrument), JSON.stringify([...visited]));
  } catch {
    // Storage unavailable (private mode): visiting still counts for this session.
  }
}

function toAnswerMap(detail: QuestionnaireDetail | null): AnswerMap {
  if (!detail) return new Map();
  return new Map(detail.answers.map((a) => [a.questionKey, a.value]));
}

function toItemEntries(detail: QuestionnaireDetail | null): Map<string, ItemEntry[]> {
  const byKey = new Map<string, ItemEntry[]>();
  if (!detail) return byKey;
  for (const item of detail.items) {
    const fields: ItemFieldMap = new Map(item.fields.map((f) => [f.fieldKey, f.value]));
    const existing = byKey.get(item.questionKey) ?? [];
    existing.push({ itemIndex: item.itemIndex, fields });
    byKey.set(item.questionKey, existing);
  }
  for (const [key, entries] of byKey) {
    byKey.set(
      key,
      [...entries].sort((a, b) => a.itemIndex - b.itemIndex),
    );
  }
  return byKey;
}

/** Owns the questionnaire's load, consent, answer state and write scheduler
    for `QuestionnaireOverlay`, `HubScreen` and `SectionScreen`. Repeating-
    group items (`iron_supplements`, `other_supplement_items`) share the same
    one-flight-at-a-time `saveAnswers` scheduler as top-level answers;
    removing an item is its own request (`deleteAnswerItem`), since there is
    no "unset" value a plain answer write could send instead. */
export function useQuestionnaire(instrument: InstrumentName): UseQuestionnaireResult {
  const api = useApi();
  const tracker = useTracker();
  const online = useOnline();
  const [state, setState] = useState<QuestionnaireViewState>("loading");
  const [detail, setDetail] = useState<ClientQuestionnaireDetail | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [localAnswers, setLocalAnswers] = useState<AnswerMap>(new Map());
  const [localItemEntries, setLocalItemEntries] = useState<Map<string, ItemEntry[]>>(new Map());
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [hasSavedAnswers, setHasSavedAnswers] = useState(false);
  const [symptomCheckDone, setSymptomCheckDone] = useState(false);
  // Tracks whether a submission is open, separately from `detail.status`:
  // grantConsent starts one without reloading the detail.
  const startedRef = useRef(false);
  const [writeStatus, setWriteStatus] = useState<"idle" | "pending" | "saving" | "failed">("idle");
  const [invalidKeys, setInvalidKeys] = useState<ReadonlySet<string>>(new Set());
  const [prefilledKeys, setPrefilledKeys] = useState<ReadonlySet<string>>(new Set());
  const prefilledKeysRef = useRef(prefilledKeys);
  prefilledKeysRef.current = prefilledKeys;
  // Held back until consent: a prefill goes dirty the moment it is applied,
  // and the write path 403s before consent.
  const pendingPrefillRef = useRef<{ version: number; prefill: QuestionnaireAnswer[] } | null>(null);
  const [visitedSectionIds, setVisitedSectionIds] = useState<ReadonlySet<string>>(() => readVisited(instrument));

  const answersRef = useRef(localAnswers);
  answersRef.current = localAnswers;
  const itemEntriesRef = useRef(localItemEntries);
  itemEntriesRef.current = localItemEntries;
  const dirtyKeysRef = useRef<Set<string>>(new Set());
  const dirtyItemFieldsRef = useRef<Set<string>>(new Set());
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flightRef = useRef<Promise<void> | null>(null);

  // Preselects each prefill whose key has no answer yet and marks it dirty,
  // so the existing flush stores it like any answer -- unsaved until then,
  // which keeps `hasSavedAnswers` (the landing decision) unaffected.
  const applyPrefill = useCallback(
    (version: number, prefill: readonly QuestionnaireAnswer[]) => {
      const fresh = prefill.filter((entry) => !answersRef.current.has(entry.questionKey));
      if (fresh.length === 0) return;
      const next = new Map(answersRef.current);
      for (const entry of fresh) {
        next.set(entry.questionKey, entry.value);
        dirtyKeysRef.current.add(entry.questionKey);
        tracker.track("questionnaire.answer_prefilled", { instrument, version, question_key: entry.questionKey });
      }
      answersRef.current = next;
      setLocalAnswers(next);
      const keys = new Set([...prefilledKeysRef.current, ...fresh.map((entry) => entry.questionKey)]);
      prefilledKeysRef.current = keys;
      setPrefilledKeys(keys);
    },
    [instrument, tracker],
  );

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    api.questionnaires
      .get(instrument)
      .then((loaded) => {
        if (cancelled) return;
        setDetail(loaded);
        setHasSavedAnswers(loaded.answers.length > 0 || loaded.items.length > 0);
        const answers = toAnswerMap(loaded);
        setLocalAnswers(answers);
        answersRef.current = answers;
        const entries = toItemEntries(loaded);
        setLocalItemEntries(entries);
        itemEntriesRef.current = entries;
        setLastSavedAt(loaded.lastSavedAt);
        setSymptomCheckDone(loaded.symptomCheckDone);
        startedRef.current = loaded.status !== "not_started";
        if (loaded.status !== "in_progress") {
          writeVisited(instrument, new Set());
          setVisitedSectionIds(new Set());
        } else {
          setVisitedSectionIds(readVisited(instrument));
        }
        dirtyKeysRef.current = new Set();
        dirtyItemFieldsRef.current = new Set();
        setWriteStatus("idle");
        prefilledKeysRef.current = new Set();
        setPrefilledKeys(prefilledKeysRef.current);
        pendingPrefillRef.current = null;
        if (loaded.status !== "submitted") {
          if (loaded.consentGranted) applyPrefill(loaded.version, loaded.prefill);
          else pendingPrefillRef.current = { version: loaded.version, prefill: loaded.prefill };
        }
        if (loaded.status === "submitted") setState("submitted");
        else if (!loaded.consentGranted) setState("consent");
        else setState("ready");
      })
      .catch(() => {
        if (!cancelled) setState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [api, instrument, reloadToken, applyPrefill]);

  const items: ItemMap = useMemo(() => {
    const map = new Map<string, readonly ItemFieldMap[]>();
    for (const [key, entries] of localItemEntries) map.set(key, entries.map((entry) => entry.fields));
    return map;
  }, [localItemEntries]);

  const itemIndexes = useMemo(() => {
    const map = new Map<string, readonly number[]>();
    for (const [key, entries] of localItemEntries) map.set(key, entries.map((entry) => entry.itemIndex));
    return map;
  }, [localItemEntries]);

  const sections = useMemo(
    () => sectionProgress(instrument, localAnswers, items),
    [instrument, localAnswers, items],
  );
  const firstUnfinishedSectionId = useMemo(
    () => firstUnfinishedSection(instrument, localAnswers, items),
    [instrument, localAnswers, items],
  );

  const completeSectionIds = useMemo(() => {
    const keysBySection = new Map(INSTRUMENT_DEFINITIONS[instrument].sections.map((s) => [s.id, s.questionKeys]));
    return new Set(
      sections
        .filter((section) =>
          section.reachable > 0
            ? section.answered === section.reachable
            : visitedSectionIds.has(section.sectionId) ||
              (keysBySection.get(section.sectionId) ?? []).some((key) => localAnswers.has(key)),
        )
        .map((section) => section.sectionId),
    );
  }, [instrument, sections, visitedSectionIds, localAnswers]);

  const markSectionVisited = useCallback(
    (sectionId: string) => {
      setVisitedSectionIds((prev) => {
        if (prev.has(sectionId)) return prev;
        const next = new Set(prev);
        next.add(sectionId);
        writeVisited(instrument, next);
        return next;
      });
    },
    [instrument],
  );

  const questionsByKey = useMemo(
    () => new Map(INSTRUMENT_DEFINITIONS[instrument].questions.map((q) => [q.key, q])),
    [instrument],
  );

  const grantConsent = useCallback(async () => {
    await api.questionnaires.start(instrument, { granted: true, documentVersion });
    startedRef.current = true;
    tracker.track("questionnaire.consent_granted", { instrument, document_version: documentVersion });
    const pending = pendingPrefillRef.current;
    pendingPrefillRef.current = null;
    if (pending) applyPrefill(pending.version, pending.prefill);
    setState("ready");
  }, [api, instrument, tracker, applyPrefill]);

  const clearDebounce = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  // One request in flight at a time: a concurrent caller gets the same
  // promise rather than a second POST, and whatever became dirty while it
  // was outstanding is picked up by the trailing flush in its `finally`.
  // Carries both dirty top-level answers and dirty repeating-group item
  // fields in the same request -- `saveAnswersRequestSchema` already has a
  // slot for both, and apps/api's route already writes them in one
  // try/catch, so there is no reason for two separate flights here. `items`
  // is only included in the payload when non-empty so an answer-only flush
  // still sends exactly the same shape it always has.
  const performFlush = useCallback((): Promise<void> => {
    clearDebounce();
    if (flightRef.current) return flightRef.current;
    const answerKeys = Array.from(dirtyKeysRef.current);
    const itemDirtyKeys = Array.from(dirtyItemFieldsRef.current);
    if (answerKeys.length === 0 && itemDirtyKeys.length === 0) return Promise.resolve();

    setWriteStatus("saving");

    // Captured by reference, not just by key: the same key can go dirty
    // again for a *newer* value while this flush is still in flight (e.g.
    // several taps on the same multi-choice question in a row). Only clear
    // a key on success if the ref still holds the exact value this flush
    // sent -- if it's moved on, the key stays dirty and the trailing flush
    // below picks up the newer value instead of silently dropping it.
    const sentAnswers = new Map<string, AnswerValue>();
    for (const key of answerKeys) {
      const value = answersRef.current.get(key);
      if (value) sentAnswers.set(key, value);
    }
    const answers = Array.from(sentAnswers, ([questionKey, value]) => ({ questionKey, value }));

    function currentItemFieldValue(questionKey: string, itemIndex: number, fieldKey: string): AnswerValue | undefined {
      return itemEntriesRef.current.get(questionKey)?.find((entry) => entry.itemIndex === itemIndex)?.fields.get(fieldKey);
    }

    const sentItemFields = new Map<string, AnswerValue>();
    const itemsByQuestion = new Map<string, Map<number, { fieldKey: string; value: AnswerValue }[]>>();
    for (const dirtyKey of itemDirtyKeys) {
      const { questionKey, itemIndex, fieldKey } = parseItemFieldKey(dirtyKey);
      const value = currentItemFieldValue(questionKey, itemIndex, fieldKey);
      if (!value) continue;
      sentItemFields.set(dirtyKey, value);
      const byIndex = itemsByQuestion.get(questionKey) ?? new Map<number, { fieldKey: string; value: AnswerValue }[]>();
      const fields = byIndex.get(itemIndex) ?? [];
      fields.push({ fieldKey, value });
      byIndex.set(itemIndex, fields);
      itemsByQuestion.set(questionKey, byIndex);
    }
    const itemsPayload = Array.from(itemsByQuestion, ([questionKey, byIndex]) =>
      Array.from(byIndex, ([itemIndex, fields]) => ({ questionKey, itemIndex, fields })),
    ).flat();

    let succeeded = false;
    const promise = api.questionnaires
      .saveAnswers(instrument, itemsPayload.length > 0 ? { answers, items: itemsPayload } : { answers })
      .then(() => {
        succeeded = true;
        online.reportSuccess();
        for (const key of answerKeys) {
          if (answersRef.current.get(key) === sentAnswers.get(key)) dirtyKeysRef.current.delete(key);
        }
        for (const dirtyKey of itemDirtyKeys) {
          const sent = sentItemFields.get(dirtyKey);
          const { questionKey, itemIndex, fieldKey } = parseItemFieldKey(dirtyKey);
          const current = currentItemFieldValue(questionKey, itemIndex, fieldKey);
          if (sent !== undefined && current === sent) dirtyItemFieldsRef.current.delete(dirtyKey);
        }
        setInvalidKeys((prev) => {
          const clearedKeys = [...answerKeys, ...itemDirtyKeys];
          if (clearedKeys.every((key) => !prev.has(key))) return prev;
          const next = new Set(prev);
          for (const key of clearedKeys) next.delete(key);
          return next;
        });
        setLastSavedAt(new Date().toISOString());
        setWriteStatus(dirtyKeysRef.current.size > 0 || dirtyItemFieldsRef.current.size > 0 ? "pending" : "idle");
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.code === "consent_required") {
          setState("consent");
          return;
        }
        if (err instanceof ApiError && err.code === "already_submitted") {
          dirtyKeysRef.current.clear();
          dirtyItemFieldsRef.current.clear();
          setState("submitted");
          return;
        }
        if (err instanceof ApiError && err.code === "invalid_answer") {
          // A numeric constraint the client should have caught -- keep the
          // batch out of the dirty set so it can't wedge the queue, and
          // surface it as a field-level error instead of a chip retry loop.
          for (const key of answerKeys) dirtyKeysRef.current.delete(key);
          for (const key of itemDirtyKeys) dirtyItemFieldsRef.current.delete(key);
          setInvalidKeys((prev) => new Set([...prev, ...answerKeys, ...itemDirtyKeys]));
          setWriteStatus(dirtyKeysRef.current.size > 0 || dirtyItemFieldsRef.current.size > 0 ? "pending" : "idle");
          tracker.track("questionnaire.save_failed", { instrument, reason: "invalid_answer" });
          return;
        }
        // Keys stay dirty either way, retried on tap or the browser's next
        // `online` event. Only a transport failure (fetch never got a
        // response -- not an ApiError, same distinction useSession.ts
        // draws) reports to useOnline: a 5xx is the server answering while
        // we're genuinely online, and should read as "failed", not
        // "offline".
        if (!(err instanceof ApiError)) online.reportFailure();
        setWriteStatus("failed");
        tracker.track("questionnaire.save_failed", { instrument, reason: "network" });
      })
      .finally(() => {
        flightRef.current = null;
        // Only chain automatically behind a *successful* flush -- keys left
        // dirty by a failure retry on the next tap or `online` event, not in
        // a loop that would hammer a server that's actually down.
        if (succeeded && (dirtyKeysRef.current.size > 0 || dirtyItemFieldsRef.current.size > 0)) void performFlush();
      });

    flightRef.current = promise;
    return promise;
  }, [api, clearDebounce, instrument, online, tracker]);

  const setAnswer = useCallback(
    (questionKey: string, value: AnswerValue) => {
      const next = new Map(answersRef.current);
      next.set(questionKey, value);
      answersRef.current = next;
      setLocalAnswers(next);
      setInvalidKeys((prev) => (prev.has(questionKey) ? new Set([...prev].filter((k) => k !== questionKey)) : prev));
      dirtyKeysRef.current.add(questionKey);
      if (prefilledKeysRef.current.has(questionKey)) {
        const remaining = new Set([...prefilledKeysRef.current].filter((k) => k !== questionKey));
        prefilledKeysRef.current = remaining;
        setPrefilledKeys(remaining);
        tracker.track("questionnaire.prefilled_answer_changed", {
          instrument,
          version: detail?.version ?? 1,
          question_key: questionKey,
        });
      }

      const question = questionsByKey.get(questionKey);
      const kind = question && !question.repeating ? question.kind : undefined;
      if (kind && IMMEDIATE_KINDS.has(kind)) {
        void performFlush();
        return;
      }
      setWriteStatus((s) => (s === "failed" ? s : "pending"));
      clearDebounce();
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        void performFlush();
      }, DEBOUNCE_MS);
    },
    [clearDebounce, performFlush, questionsByKey, tracker, instrument, detail],
  );

  const saveItem = useCallback(
    (questionKey: string, itemIndex: number | null, fields: ReadonlyMap<string, AnswerValue>): boolean => {
      const entries = itemEntriesRef.current.get(questionKey) ?? [];
      const index = itemIndex ?? entries.reduce((max, entry) => Math.max(max, entry.itemIndex + 1), 0);
      const stored = entries.find((entry) => entry.itemIndex === index)?.fields ?? new Map<string, AnswerValue>();
      const changed = [...fields].filter(([fieldKey, value]) => !sameAnswerValue(stored.get(fieldKey), value));
      if (changed.length === 0) return false;

      const nextFields = new Map(stored);
      for (const [fieldKey, value] of changed) nextFields.set(fieldKey, value);
      const nextEntries =
        itemIndex === null
          ? [...entries, { itemIndex: index, fields: nextFields }]
          : entries.map((entry) => (entry.itemIndex === index ? { itemIndex: index, fields: nextFields } : entry));
      const nextMap = new Map(itemEntriesRef.current);
      nextMap.set(questionKey, nextEntries);
      itemEntriesRef.current = nextMap;
      setLocalItemEntries(nextMap);

      const dirtyKeys = changed.map(([fieldKey]) => itemFieldKey(questionKey, index, fieldKey));
      setInvalidKeys((prev) =>
        dirtyKeys.some((key) => prev.has(key)) ? new Set([...prev].filter((k) => !dirtyKeys.includes(k))) : prev,
      );
      for (const key of dirtyKeys) dirtyItemFieldsRef.current.add(key);
      void performFlush();
      return true;
    },
    [performFlush],
  );

  const removeItem = useCallback(
    (questionKey: string, itemIndex: number) => {
      const entries = itemEntriesRef.current.get(questionKey) ?? [];
      const nextMap = new Map(itemEntriesRef.current);
      nextMap.set(
        questionKey,
        entries.filter((entry) => entry.itemIndex !== itemIndex),
      );
      itemEntriesRef.current = nextMap;
      setLocalItemEntries(nextMap);

      // Nothing left to flush an edit onto once the item is gone.
      for (const dirtyKey of [...dirtyItemFieldsRef.current]) {
        const parsed = parseItemFieldKey(dirtyKey);
        if (parsed.questionKey === questionKey && parsed.itemIndex === itemIndex) {
          dirtyItemFieldsRef.current.delete(dirtyKey);
        }
      }

      setWriteStatus((s) => (s === "failed" ? s : "pending"));
      api.questionnaires
        .deleteItem(instrument, questionKey, itemIndex)
        .then(() => {
          online.reportSuccess();
          setWriteStatus((s) =>
            s === "failed" ? s : dirtyKeysRef.current.size > 0 || dirtyItemFieldsRef.current.size > 0 ? "pending" : "idle",
          );
        })
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.code === "consent_required") {
            setState("consent");
            return;
          }
          if (err instanceof ApiError && err.code === "already_submitted") {
            setState("submitted");
            return;
          }
          if (!(err instanceof ApiError)) online.reportFailure();
          setWriteStatus("failed");
          tracker.track("questionnaire.save_failed", { instrument, reason: "network" });
        });
    },
    [api, instrument, online, tracker],
  );

  const flush = useCallback(async () => {
    clearDebounce();
    await performFlush();
    // Answers written while that flush was in flight land in a second
    // round -- performFlush's own trailing call handles the general case,
    // but a caller awaiting flush() (leaving a section, closing, submit)
    // needs the settling itself, not just the kick-off.
    if (dirtyKeysRef.current.size > 0 || dirtyItemFieldsRef.current.size > 0) await performFlush();
  }, [clearDebounce, performFlush]);

  const retrySave = useCallback(() => {
    void performFlush();
  }, [performFlush]);

  const retry = useCallback(() => setReloadToken((n) => n + 1), []);

  const markSymptomCheckDone = useCallback(() => setSymptomCheckDone(true), []);

  const ensureStarted = useCallback(async () => {
    if (startedRef.current) return;
    await api.questionnaires.start(instrument);
    startedRef.current = true;
  }, [api, instrument]);

  const submit = useCallback(async () => {
    await flush();
    try {
      await api.questionnaires.submit(instrument);
      writeVisited(instrument, new Set());
      setState("submitted");
    } catch (err) {
      if (err instanceof ApiError && err.code === "already_submitted") {
        setState("submitted");
        return;
      }
      throw err;
    }
  }, [api, flush, instrument]);

  const delayedSaving = useDelayedFlag(writeStatus === "saving", 400);

  // Auto-retry once on the browser's own `online` event; otherwise the
  // failed chip waits for a tap. `navigator.onLine` alone isn't a
  // trustworthy positive signal (useOnline's own doc comment), but the
  // event firing at all is real news worth acting on.
  useEffect(() => {
    function handleOnline() {
      if (writeStatus === "failed") void performFlush();
    }
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [writeStatus, performFlush]);

  const save: SaveState = useMemo(() => {
    if (writeStatus === "idle") return "saved";
    if (!online.online) return "offline";
    if (writeStatus === "failed") return "failed";
    if (delayedSaving) return "saving";
    return "saved";
  }, [writeStatus, online.online, delayedSaving]);

  return {
    state,
    version: detail?.version ?? 1,
    answers: localAnswers,
    items,
    itemIndexes,
    sections,
    firstUnfinishedSectionId,
    completeSectionIds,
    visitedSectionIds,
    markSectionVisited,
    hasSavedAnswers,
    lastSavedAt,
    symptomCheckDone,
    markSymptomCheckDone,
    ensureStarted,
    save,
    invalidKeys,
    prefilledKeys,
    grantConsent,
    setAnswer,
    removeItem,
    saveItem,
    flush,
    retrySave,
    retry,
    submit,
  };
}
